/**
 * @file Creation d'une PlayerData neuve (valeurs de depart issues de la config). Source des valeurs par defaut des migrations.
 */
import { Decimal } from '@shared/bignum';
import type { DecimalSource } from '@shared/bignum';
import { UPGRADE_IDS, WALLET_IDS } from '@shared/enums';
import type { UpgradeId, WalletId } from '@shared/enums';
import type { GameConfig } from '@config/index';
import { DEFAULT_AMMO_SKIN, DEFAULT_FIREWORK, DEFAULT_SKIN_ID, STARTER_CHEST_WEAPON_IDS, STARTER_WEAPON_ID } from '@config/index';
import type { BuffId, MetricId, TicketId } from '@config/types';
import type { PlayerData, WeaponItem } from '../state/PlayerData';

const METRICS: MetricId[] = [
  'treeKills', 'monsterKills', 'stageClears', 'rebirths', 'gunDraws', 'gunMerges', 'minePlays',
  'moneyEarned', 'bossKills', 'upgradesBought', 'wavesCleared', 'resourcesHarvested',
];

export interface NewGameOptions {
  seed: number;
  nowMs: number;
  playerId: string;
  deviceId: string;
  locale: 'fr' | 'en';
}

export const createNewPlayerData = (cfg: GameConfig, o: NewGameOptions): PlayerData => {
  const wallet = Object.fromEntries(WALLET_IDS.map((id) => [id, new Decimal(0)])) as Record<WalletId, Decimal>;
  for (const [id, v] of Object.entries(cfg.balance.startWallet) as Array<[WalletId, DecimalSource]>) wallet[id] = Decimal.fromValue(v);

  const starters: WeaponItem[] = [STARTER_WEAPON_ID, ...STARTER_CHEST_WEAPON_IDS].map((defId, i) => ({
    uid: `w${i + 1}`,
    defId,
    locked: false,
    obtainedSeq: i + 1,
  }));
  const slots = cfg.balance.equipSlotUnlocks.length;
  const equipped: Array<string | null> = Array.from({ length: slots }, () => null);
  equipped[0] = starters[0]?.uid ?? null;

  const tickets = Object.fromEntries(
    (Object.keys(cfg.balance.tickets) as TicketId[]).map((t) => [t, { count: cfg.balance.tickets[t].startCount, lastRegenMs: o.nowMs }]),
  ) as PlayerData['tickets'];
  const buffs = Object.fromEntries(cfg.balance.buffs.map((b) => [b.id, { seconds: 0, superActive: false, lastFreeMs: 0 }])) as Record<BuffId, PlayerData['buffs'][BuffId]>;
  const collection: PlayerData['weapons']['collection'] = {};
  for (const w of starters) collection[w.defId] = { seen: true, obtained: 1 };

  return {
    meta: {
      playerId: o.playerId,
      deviceId: o.deviceId,
      nickname: '',
      createdAtMs: o.nowMs,
      lastSavedAtMs: o.nowMs,
      lastSeenMs: o.nowMs,
      seed: o.seed >>> 0,
      revision: 0,
    },
    wallet,
    run: {
      upgrades: Object.fromEntries(UPGRADE_IDS.map((u) => [u, cfg.balance.upgrades.startLevel])) as Record<UpgradeId, number>,
      moneyAccum: new Decimal(0),
      stageValue: 0,
      bestStageValue: 0,
      mainPhase: 'challenge',
      playTimeSeconds: 0,
    },
    progress: { bestStageValue: 0, modeBests: {}, sweepUnlocked: {}, totalKills: 0 },
    weapons: {
      items: starters,
      equipped,
      chestPaidSlots: 0,
      collection,
      collectionClaimed: [],
      pity: { normal: 0, epic: 0 },
      autoMerge: false,
      nextSeq: starters.length + 1,
      dailyFreeDrawDay: -1,
    },
    stars: { levels: Array.from({ length: slots }, () => 0), pityFails: Array.from({ length: slots }, () => 0) },
    skins: { owned: [DEFAULT_SKIN_ID], equipped: DEFAULT_SKIN_ID },
    auras: { owned: [], equipped: null, enabled: true },
    fireworks: { owned: [DEFAULT_FIREWORK], equipped: DEFAULT_FIREWORK },
    shotFx: { owned: [], equipped: null },
    hub: { catnip: 0 },
    ammoSkins: { owned: Object.values(DEFAULT_AMMO_SKIN), equipped: { ...DEFAULT_AMMO_SKIN } },
    buffs,
    rebirth: { count: 0, levels: { damage: 0, attackSpeed: 0, money: 0 }, dailyBonusDay: -1, totalRubyEarned: new Decimal(0) },
    relics: { levels: {} },
    pets: { owned: {}, equipped: [] },
    fish: { owned: [] },
    adventure: { expeditions: [], level: 1, xp: 0 },
    tickets,
    missions: {
      daily: [],
      weekly: [],
      achievements: {},
      dayKey: -1,
      weekKey: -1,
      lifetime: Object.fromEntries(METRICS.map((m) => [m, 0])) as Record<MetricId, number>,
    },
    pass: { seasonId: -1, xp: 0, epicUnlocked: false, claimedFree: [], claimedEpic: [] },
    daily: { nextDay: 1, lastClaimDayKey: -1 },
    offline: { pending: null },
    settings: {
      locale: o.locale,
      autoMove: true,
      autoFire: true,
      sfxVolume: 0.8,
      musicVolume: 0.5,
      muted: false,
      eco: false,
      quality: 'high',
      vibration: true,
      damageNumbers: true,
      reduceMotion: false,
      autoUpgrade: false,
      fxSelf: true,
      fxOthers: true,
    },
    tutorial: { done: false, stepIndex: 0 },
    flags: {},
  };
};
